interface AllergenKey {
  key: string;
}

export interface RecipeAllergenFormState {
  // Manual allergens (the only ones the user can remove)
  selfAllergens: readonly string[];
  // Allergens inherited from child recipes, read-only
  inheritedAllergens: readonly string[];
  // Confirmed allergen free, as opposed to not yet reviewed
  allergenFree: boolean;
}

export const emptyAllergenFormState = (): RecipeAllergenFormState => ({
  selfAllergens: [],
  inheritedAllergens: [],
  allergenFree: false,
});

export const allergenFormStateFromRecipe = (recipe: {
  allergens?: readonly AllergenKey[] | null;
  selfAllergens?: readonly AllergenKey[] | null;
}): RecipeAllergenFormState => {
  const selfAllergens = (recipe.selfAllergens ?? []).map(a => a.key);
  return {
    selfAllergens,
    inheritedAllergens: (recipe.allergens ?? []).map(a => a.key).filter(key => !selfAllergens.includes(key)),
    allergenFree: recipe.selfAllergens?.length === 0,
  };
};

export const effectiveAllergens = ({
  selfAllergens,
  inheritedAllergens,
}: Pick<RecipeAllergenFormState, "selfAllergens" | "inheritedAllergens">): string[] => [
  ...new Set([...selfAllergens, ...inheritedAllergens]),
];

export const canSelectAllergenFree = (state: Pick<RecipeAllergenFormState, "selfAllergens" | "inheritedAllergens">): boolean =>
  effectiveAllergens(state).length === 0;

export const changeSelfAllergens = <T extends RecipeAllergenFormState>(state: T, selfAllergens: readonly string[]): T => ({
  ...state,
  selfAllergens,
  allergenFree: selfAllergens.length > 0 ? false : state.allergenFree,
});

export const changeAllergenFree = <T extends RecipeAllergenFormState>(state: T, allergenFree: boolean): T => ({
  ...state,
  allergenFree: allergenFree && canSelectAllergenFree(state),
});

// null = not yet reviewed, [] = confirmed allergen free
export const selfAllergensInput = (state: RecipeAllergenFormState): string[] | null => {
  if (state.selfAllergens.length > 0) {
    return [...state.selfAllergens];
  }
  return state.allergenFree && canSelectAllergenFree(state) ? [] : null;
};

export const allergensChanged = (initial: RecipeAllergenFormState, current: RecipeAllergenFormState): boolean => {
  const a = selfAllergensInput(initial);
  const b = selfAllergensInput(current);
  if (a === null || b === null) {
    return a !== b;
  }
  return [...a].sort().join() !== [...b].sort().join();
};
